import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, MessageSquare } from 'lucide-react';
import {
  Builder,
  Eyebrow,
  HandwritingReveal,
  MarginNote,
  MarkerCircle,
  MarkerHighlight,
  MarkerUnderline,
  RaceBib,
  Tom,
} from '@/components/foundry';
import {
  ACQUISITION_NEXT_MOVE,
  ACQUISITION_PROGRESS,
  ACQUISITION_STAGES,
  type AcquisitionStage,
  type StageState,
} from '../lib/acquisitionOsFixtures';

// ─────────────────────────────────────────────────────────────────────────
// AI Acquisition OS · creative workbook
//
// The three Foundry brand layers stacked:
//   1. Chrome — engineered SaaS race-bib (yellow stamps, mono numerics)
//   2. Scribble — workshop marker work (Caveat margin notes, marker circles,
//      highlight underlines) — annotates, never decorates
//   3. Characters — Tom (AI Co-Founder) + Builder (the customer) earn moments
//
// Typography mix:
//   - Geist Sans for the structural type
//   - Caveat (handwritten) for accent words, margin notes, "you are here" cues
//
// World-class UX rule still applies: ONE clear next move; coach-friendly
// language; no engineering jargon.
// ─────────────────────────────────────────────────────────────────────────

export type EngineKind = 'acquisition' | 'experience';

interface Props {
  engine: EngineKind;
}

export default function EngineWorkspace({ engine }: Props) {
  if (engine === 'experience') return <CoachingOsLane />;
  return <AcquisitionOsLane />;
}

// ─────────────────────────────────────────────────────────────────────────
// AI Acquisition OS lane
// ─────────────────────────────────────────────────────────────────────────

function AcquisitionOsLane() {
  const prefersReducedMotion = useReducedMotion();
  return (
    <div className="mx-auto max-w-[1100px] px-6 pb-24 pt-10 sm:px-10 sm:pt-14">
      <BackHome />
      <ProductHeader />
      <Hero reducedMotion={Boolean(prefersReducedMotion)} />
      <StageList reducedMotion={Boolean(prefersReducedMotion)} />
      <SupportFooter reducedMotion={Boolean(prefersReducedMotion)} />
    </div>
  );
}

function BackHome() {
  return (
    <a
      href="?osRoute=lab-home"
      className="group inline-flex items-center gap-1.5 font-geist-mono text-[11px] font-bold uppercase tracking-[0.06em] text-fd-text-muted transition-colors hover:text-fd-text"
    >
      <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" strokeWidth={2} />
      All products
    </a>
  );
}

function ProductHeader() {
  return (
    <header className="mt-3">
      <div className="flex items-center gap-2.5">
        <span style={{ transform: 'rotate(-2deg)', display: 'inline-block' }}>
          <RaceBib n="V1" tone="race" size="sm" shadow="sm" />
        </span>
        <Eyebrow>
          Your acquisition system{' '}
          <span className="font-caveat text-[18px] font-medium normal-case tracking-normal text-fd-teal">
            · built with you, not for you
          </span>
        </Eyebrow>
      </div>
    </header>
  );
}

// ── Hero — workbook-style with Tom + Caveat + marker work ──────────────

function Hero({ reducedMotion }: { reducedMotion: boolean }) {
  const move = ACQUISITION_NEXT_MOVE;
  const stage = move.stage;

  return (
    <section className="relative mt-7 sm:mt-10">
      <ProgressBar />

      <div className="relative mt-10 grid items-end gap-x-10 gap-y-8 lg:grid-cols-[1fr_240px]">
        <div className="min-w-0">
          {/* Eyebrow with handwritten "today" tag */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.10em] text-fd-text-subtle">
              Stage {stage.num} · {stage.duration}
            </span>
            <span className="font-caveat text-[20px] font-medium leading-none text-fd-coral" style={{ transform: 'rotate(-3deg)', display: 'inline-block' }}>
              today
            </span>
          </div>

          {/* Mixed-type headline: structural "Pick your" + payoff handwritten "Channel" */}
          <h1 className="mt-3 [text-wrap:balance]">
            <span className="block font-geist text-[28px] font-medium leading-[1.05] tracking-[-0.015em] text-fd-text-muted sm:text-[36px]">
              Pick your
            </span>
            <span className="mt-1 inline-block pr-3">
              <MarkerUnderline tone="race" strokeWidth={5} duration={900} delay={1700}>
                <HandwritingReveal
                  duration={1.4}
                  delay={0.2}
                  className="font-caveat text-[78px] font-medium leading-[0.88] tracking-normal text-fd-text sm:text-[120px]"
                  style={{ transform: 'rotate(-1.5deg)', transformOrigin: 'left center', paddingRight: '0.15em' }}
                >
                  {stage.name}
                </HandwritingReveal>
              </MarkerUnderline>
              <span className="font-geist text-[40px] font-medium text-fd-text sm:text-[60px]">.</span>
            </span>
          </h1>

          <p className="mt-5 max-w-[600px] text-[16px] leading-[1.55] text-fd-text-muted [text-wrap:pretty]">
            {move.body}
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-3">
            <a
              href={`#stage-${stage.num}`}
              className="inline-flex h-11 items-center gap-2 rounded-[6px] border-[1.5px] border-fd-text bg-fd-accent px-5 font-geist-mono text-[12.5px] font-bold uppercase tracking-[0.04em] text-fd-text transition-transform hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fd-text"
              style={{ boxShadow: '2px 2px 0 0 var(--fd-text)' }}
            >
              {move.ctaLabel}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </a>
            {move.reviewHint && (
              <MarginNote tilt={-2} tone="teal" size="sm" className="inline-block">
                tom reviews this with you.
              </MarginNote>
            )}
          </div>

          {/* Workshop annotation — "this is where most builders find their wedge" */}
          <div className="mt-7 inline-flex items-start gap-2.5">
            <span aria-hidden className="mt-2 block h-px w-10 bg-fd-coral/60" />
            <MarginNote tilt={-2} tone="coral" size="sm">
              this stage is where most builders find their wedge.
            </MarginNote>
          </div>
        </div>

        {/* Tom — AI Co-Founder beside the Builder */}
        <motion.div
          aria-hidden
          initial={reducedMotion ? false : { opacity: 0, y: 10 }}
          animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="relative hidden w-full justify-self-end lg:block"
        >
          <MarginNote
            tilt={6}
            tone="teal"
            size="sm"
            className="absolute -top-4 left-2"
          >
            tom's here ✱
          </MarginNote>
          <Tom variant="pointing-right" className="block w-full" style={{ transform: 'scaleX(-1)' }} />
        </motion.div>
      </div>
    </section>
  );
}

// ── Progress bar ────────────────────────────────────────────────────────

function ProgressBar() {
  const { total, done, currentIndex } = ACQUISITION_PROGRESS;
  const fillFraction = currentIndex >= 0 ? (done + 0.5) / total : done / total;

  return (
    <div className="flex items-center gap-4">
      <div className="relative h-1 flex-1 overflow-hidden rounded-full bg-fd-surface-2">
        <div
          className="absolute inset-y-0 left-0 bg-fd-text"
          style={{ width: `${fillFraction * 100}%` }}
        />
      </div>
      <span className="shrink-0 font-caveat text-[18px] font-medium text-fd-text-muted">
        {done} of {total}
      </span>
    </div>
  );
}

// ── Stage list — workbook curriculum ───────────────────────────────────

function StageList({ reducedMotion }: { reducedMotion: boolean }) {
  return (
    <section className="mt-16">
      <div className="mb-5 flex items-baseline justify-between gap-4">
        <h2 className="flex items-baseline gap-2 font-geist text-[12.5px] font-semibold uppercase tracking-[0.10em] text-fd-text">
          Your{' '}
          <span className="font-caveat text-[26px] normal-case tracking-normal text-fd-coral" style={{ transform: 'rotate(-2deg)', display: 'inline-block' }}>
            8
          </span>{' '}
          stages
        </h2>
        <span className="font-caveat text-[18px] font-medium text-fd-text-subtle">
          start to live
        </span>
      </div>

      <ol className="divide-y divide-fd-border border-y border-fd-border">
        {ACQUISITION_STAGES.map((stage, idx) => (
          <StageRow key={stage.num} stage={stage} reducedMotion={reducedMotion} index={idx} />
        ))}
      </ol>
    </section>
  );
}

function StageRow({
  stage,
  reducedMotion,
  index,
}: {
  stage: AcquisitionStage;
  reducedMotion: boolean;
  index: number;
}) {
  const isCurrent = stage.state === 'current';
  const isDone = stage.state === 'done';
  const isInteractive = stage.state !== 'upcoming';

  const Inner = (
    <div className="relative flex items-center gap-5 py-5">
      <span
        className={cls(
          'grid h-9 w-9 shrink-0 place-items-center rounded-full font-geist-mono text-[12px] font-bold tabular-nums transition-colors',
          isDone
            ? 'bg-fd-text text-fd-paper'
            : isCurrent
            ? 'border-[1.5px] border-fd-text bg-fd-accent text-fd-text shadow-[2px_2px_0_0_var(--fd-text)]'
            : 'border border-fd-border bg-fd-paper text-fd-text-subtle',
        )}
        style={isCurrent ? { transform: 'rotate(-3deg)' } : undefined}
      >
        {isDone ? <Check className="h-4 w-4" strokeWidth={2.5} /> : stage.num}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <h3
            className={cls(
              'font-geist text-[17px] font-medium tracking-[-0.01em]',
              isCurrent ? 'text-fd-text' : isDone ? 'text-fd-text' : 'text-fd-text-muted',
            )}
          >
            {isCurrent ? (
              <MarkerHighlight tone="race" duration={900} delay={400}>
                <span>{stage.name}</span>
              </MarkerHighlight>
            ) : (
              stage.name
            )}
          </h3>
          <StageStatus state={stage.state} />
        </div>
        <p
          className={cls(
            'mt-1 text-[13.5px] leading-[1.5] [text-wrap:pretty]',
            isCurrent || isDone ? 'text-fd-text-muted' : 'text-fd-text-subtle',
          )}
        >
          {stage.description}
        </p>
      </div>

      <span
        className={cls(
          'hidden shrink-0 font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] sm:inline-flex',
          isCurrent ? 'text-fd-text' : 'text-fd-text-subtle',
        )}
      >
        {stage.duration}
      </span>

      {isInteractive ? (
        <ArrowRight
          className={cls(
            'h-4 w-4 shrink-0 transition-transform',
            isCurrent ? 'text-fd-text' : 'text-fd-text-subtle',
          )}
          strokeWidth={2}
        />
      ) : (
        <span className="h-4 w-4 shrink-0" aria-hidden />
      )}

      {/* Workshop annotation on current stage row */}
      {isCurrent && (
        <motion.span
          aria-hidden
          initial={reducedMotion ? false : { opacity: 0, x: -8 }}
          animate={reducedMotion ? undefined : { opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
          className="absolute -right-3 top-2 hidden lg:block"
        >
          <MarginNote tilt={-6} tone="teal" size="sm">
            ← you're here
          </MarginNote>
        </motion.span>
      )}

      {/* Subtle "nice." annotation on the most recently done stage */}
      {isDone && index === 0 && (
        <span aria-hidden className="absolute -left-1 top-1.5 hidden lg:block">
          <MarginNote tilt={-8} tone="ink" size="sm" className="opacity-70">
            nice.
          </MarginNote>
        </span>
      )}
    </div>
  );

  return (
    <li id={`stage-${stage.num}`}>
      {isInteractive ? (
        <a
          href={`#stage-${stage.num}`}
          className={cls(
            'group block transition-colors',
            isCurrent ? 'bg-fd-accent/10 hover:bg-fd-accent/15' : 'hover:bg-fd-surface-1/40',
          )}
        >
          {Inner}
        </a>
      ) : (
        <div className="block">{Inner}</div>
      )}
    </li>
  );
}

function StageStatus({ state }: { state: StageState }) {
  if (state === 'done') {
    return (
      <span className="inline-flex h-5 items-center gap-1.5 rounded-[3px] bg-fd-surface-2 px-1.5 font-geist-mono text-[9.5px] font-bold uppercase leading-none tracking-[0.06em] text-fd-text-muted">
        Done
      </span>
    );
  }
  if (state === 'current') {
    return (
      <MarkerCircle tone="teal" strokeWidth={2} duration={900} delay={500} padding={4}>
        <span className="inline-flex h-5 items-center gap-1.5 rounded-[3px] border border-fd-text bg-fd-accent px-1.5 font-geist-mono text-[9.5px] font-bold uppercase leading-none tracking-[0.06em] text-fd-text">
          Up next
        </span>
      </MarkerCircle>
    );
  }
  return (
    <span className="inline-flex h-5 items-center gap-1.5 rounded-[3px] px-1.5 font-geist-mono text-[9.5px] font-bold uppercase leading-none tracking-[0.06em] text-fd-text-subtle">
      Coming up
    </span>
  );
}

// ── Support footer — workbook-feel ─────────────────────────────────────

function SupportFooter({ reducedMotion }: { reducedMotion: boolean }) {
  return (
    <footer className="relative mt-16 flex flex-col gap-4 border-t border-fd-border pt-7 sm:flex-row sm:items-end sm:justify-between">
      <div className="relative flex-1">
        <p className="text-[14.5px] leading-[1.5] text-fd-text [text-wrap:pretty]">
          Stuck or unsure?{' '}
          <MarkerUnderline tone="teal" strokeWidth={2} duration={700} delay={200}>
            <span className="font-medium">Ask The Lab</span>
          </MarkerUnderline>{' '}
          — Tom and the team carry your stage and context automatically.
        </p>
        <MarginNote tilt={-2} tone="ink" size="sm" className="mt-2 inline-block opacity-80">
          we usually reply same day.
        </MarginNote>
      </div>

      <div className="flex items-center gap-4">
        <a
          href="?osRoute=lab"
          className="inline-flex h-10 items-center gap-1.5 self-start rounded-[5px] border-[1.5px] border-fd-text bg-fd-paper px-4 font-geist-mono text-[11.5px] font-bold uppercase tracking-[0.04em] text-fd-text transition-colors hover:bg-fd-accent hover:text-[oklch(0.16_0.005_95)] sm:self-auto"
          style={{ boxShadow: '2px 2px 0 0 var(--fd-text)' }}
        >
          <MessageSquare className="h-3.5 w-3.5" strokeWidth={2} />
          Ask The Lab
        </a>

        {/* Builder peeking — supportive, not decorative */}
        <motion.div
          aria-hidden
          initial={reducedMotion ? false : { opacity: 0, y: 10 }}
          animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="hidden h-[88px] w-[88px] sm:block"
        >
          <Builder variant="reading-notebook" className="block h-full w-auto" />
        </motion.div>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Coaching OS lane (placeholder — second strategic product)
// ─────────────────────────────────────────────────────────────────────────

function CoachingOsLane() {
  return (
    <div className="mx-auto max-w-[820px] px-6 pb-24 pt-10 sm:px-10 sm:pt-14">
      <BackHome />

      <header className="mt-3">
        <div className="flex items-center gap-2.5">
          <RaceBib n="02" tone="outline" size="sm" shadow="sm" />
          <Eyebrow>Second strategic product</Eyebrow>
        </div>
        <h1 className="mt-3 font-geist text-[44px] font-medium leading-[1.02] tracking-[-0.025em] text-fd-text [text-wrap:balance] sm:text-[52px]">
          Coaching OS.
        </h1>
        <p className="mt-3 max-w-[640px] text-[16px] leading-[1.55] text-fd-text-muted">
          AI-native member experience, proof, method-guided support, and operator intelligence —
          additive on top of Skool, Kajabi, or Circle, or as a full member dashboard. Ships after AI
          Acquisition OS is in market.
        </p>
      </header>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(' ');
}
